import { supabase } from "./supabase";
import type {
  AdminUser,
  AdminUserDetail,
  OrderStatus,
  UserFilter,
} from "./types";

/**
 * Every server call the panel makes, in one place.
 *
 * Two rules here, both learned from what this replaced:
 *
 *   1. ERRORS THROW. The old pages wrote `await supabase.from(x).update(y)`
 *      and ignored the returned error, then updated local state as though it
 *      had worked. With RLS in front of these tables that is the difference
 *      between "the panel works" and "the panel appears to work" — a denied
 *      write looked identical to a successful one until you reloaded.
 *
 *   2. PRIVILEGED WRITES GO THROUGH RPCs. `admin_*` functions check
 *      authorisation server-side and record who did what (migration 0008).
 *      A direct `.update()` from here would be unlogged and would need a
 *      matching admin write policy — which is a much wider door than a verb.
 */

/** Unwraps a supabase result, throwing on error so useQuery can catch it. */
function unwrap<T>(result: { data: T | null; error: { message: string } | null }): T {
  if (result.error) throw new Error(result.error.message);
  return result.data as T;
}

// ── Users ───────────────────────────────────────────────────────────────────

export async function fetchUsers(params: {
  search: string;
  filter: UserFilter;
  limit: number;
  offset: number;
}): Promise<{ rows: AdminUser[]; total: number }> {
  const rows = unwrap(
    await supabase.rpc("admin_users", {
      p_search: params.search || null,
      p_filter: params.filter,
      p_limit: params.limit,
      p_offset: params.offset,
    })
  ) as AdminUser[];

  // total_count is a window function over the filtered set, so it's identical
  // on every row and absent when there are none.
  return { rows: rows ?? [], total: rows?.[0]?.total_count ?? 0 };
}

export async function fetchUserDetail(userId: string): Promise<AdminUserDetail> {
  return unwrap(
    await supabase.rpc("admin_user_detail", { p_user: userId })
  ) as AdminUserDetail;
}

export async function setSuspension(
  userId: string,
  suspended: boolean,
  reason?: string
): Promise<void> {
  const { error } = await supabase.rpc("admin_set_suspension", {
    p_user: userId,
    p_suspended: suspended,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function setPremium(
  userId: string,
  premium: boolean,
  note?: string
): Promise<void> {
  const { error } = await supabase.rpc("admin_set_premium", {
    p_user: userId,
    p_premium: premium,
    p_note: note ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function setAdmin(userId: string, admin: boolean): Promise<void> {
  const { error } = await supabase.rpc("admin_set_admin", {
    p_user: userId,
    p_admin: admin,
  });
  if (error) throw new Error(error.message);
}

// ── Community ───────────────────────────────────────────────────────────────

export type ModerationFilter = "all" | "tutorials" | "media" | "text";

export type ModPost = {
  id: string;
  user_id: string;
  content: string | null;
  topics: string[] | null;
  hashtags: string[] | null;
  likes_count: number;
  comments_count: number;
  saves_count: number;
  has_media: boolean;
  media_count: number;
  created_at: string;
  profiles: { full_name: string | null; username: string | null; avatar_url: string | null } | null;
  post_media: {
    id: string;
    kind: "image" | "video";
    url: string;
    thumbnail_url: string | null;
    position: number;
  }[];
};

export async function fetchPosts(params: {
  search: string;
  filter: ModerationFilter;
  limit: number;
  offset: number;
}): Promise<{ rows: ModPost[]; total: number }> {
  // Selected through a join rather than an RPC: `posts` already has an admin
  // read policy, and the author columns used here are all inside the allow-list
  // 0010 leaves readable. Adding an RPC would be a second definition of the
  // same query with nothing gained.
  let query = supabase
    .from("posts")
    .select(
      `id, user_id, content, topics, hashtags, likes_count, comments_count,
       saves_count, has_media, media_count, created_at,
       profiles(full_name, username, avatar_url),
       post_media(id, kind, url, thumbnail_url, position)`,
      { count: "exact" }
    )
    .order("created_at", { ascending: false })
    .range(params.offset, params.offset + params.limit - 1);

  if (params.filter === "media") query = query.eq("has_media", true);
  if (params.filter === "text") query = query.eq("has_media", false);
  if (params.filter === "tutorials") query = query.contains("topics", ["Tutorial"]);

  const term = params.search.trim();
  if (term) {
    // Same escaping as the app's search: PostgREST reads %, commas, parens and
    // braces as filter syntax, so raw input could rewrite the query it sits in.
    const safe = term.replace(/[%,(){}"'\\]/g, " ").trim();
    if (safe) query = query.ilike("content", `%${safe}%`);
  }

  const { data, error, count } = await query;
  if (error) throw new Error(error.message);
  return { rows: (data ?? []) as unknown as ModPost[], total: count ?? 0 };
}

export type ModComment = {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  profiles: { full_name: string | null; username: string | null } | null;
};

export async function fetchPostComments(postId: string): Promise<ModComment[]> {
  const { data, error } = await supabase
    .from("comments")
    .select("id, user_id, content, created_at, profiles(full_name, username)")
    .eq("post_id", postId)
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as ModComment[];
}

// ── Audit log ───────────────────────────────────────────────────────────────

export type AuditEntry = {
  id: string;
  admin_id: string;
  action: string;
  target_table: string | null;
  target_id: string | null;
  detail: Record<string, any>;
  created_at: string;
  profiles: { full_name: string | null } | null;
};

export async function fetchAuditLog(params: {
  action: string;
  limit: number;
  offset: number;
}): Promise<{ rows: AuditEntry[]; total: number }> {
  let query = supabase
    .from("admin_actions")
    .select("id, admin_id, action, target_table, target_id, detail, created_at, profiles!admin_actions_admin_id_fkey(full_name)", {
      count: "exact",
    })
    .order("created_at", { ascending: false })
    .range(params.offset, params.offset + params.limit - 1);

  if (params.action !== "all") query = query.eq("action", params.action);

  const { data, error, count } = await query;
  if (error) throw new Error(error.message);
  return { rows: (data ?? []) as unknown as AuditEntry[], total: count ?? 0 };
}

// ── Moderation ──────────────────────────────────────────────────────────────

export async function deletePost(postId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc("admin_delete_post", {
    p_post: postId,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function deleteComment(
  commentId: string,
  reason?: string
): Promise<void> {
  const { error } = await supabase.rpc("admin_delete_comment", {
    p_comment: commentId,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
}

/** Replies are their own table, so they need their own delete. */
export async function deleteReply(replyId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc("admin_delete_reply", {
    p_reply: replyId,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
}

// ── Reports (App Store guideline 1.2) ───────────────────────────────────────

export type ReportStatus = "open" | "actioned" | "dismissed";

export type ReportRow = {
  id: string;
  reporter_id: string | null;
  target_type: "post" | "comment" | "reply" | "user";
  target_id: string;
  target_user_id: string | null;
  reason: string;
  details: string | null;
  status: ReportStatus;
  created_at: string;
  reviewed_at: string | null;
  admin_notes: string | null;
  /** Joined author, for the queue. Null once an account is deleted. */
  target_user: { username: string | null; full_name: string | null } | null;
  reporter: { username: string | null; full_name: string | null } | null;
};

export async function fetchReports(opts: {
  status: ReportStatus | "all";
  offset: number;
  limit: number;
}): Promise<{ rows: ReportRow[]; total: number }> {
  let query = supabase
    .from("reports")
    .select(
      `*,
       target_user:target_user_id(username, full_name),
       reporter:reporter_id(username, full_name)`,
      { count: "exact" }
    )
    // Oldest first: a moderation queue is worked front to back, and the
    // report that has been waiting longest is the one most likely to matter.
    .order("created_at", { ascending: true })
    .range(opts.offset, opts.offset + opts.limit - 1);

  if (opts.status !== "all") query = query.eq("status", opts.status);

  const { data, error, count } = await query;
  if (error) throw new Error(error.message);
  return { rows: (data ?? []) as unknown as ReportRow[], total: count ?? 0 };
}

/** The reported post or comment, so a decision isn't made from a reason code. */
export async function fetchReportedContent(
  targetType: ReportRow["target_type"],
  targetId: string
): Promise<{ content: string | null; author: string | null } | null> {
  if (targetType === "user") return null;
  const table =
    targetType === "post" ? "posts" : targetType === "comment" ? "comments" : "replies";

  const { data, error } = await supabase
    .from(table)
    .select("content, profiles(username)")
    .eq("id", targetId)
    .maybeSingle();

  // A deleted row is not an error — it means somebody got there first.
  if (error) return null;
  if (!data) return null;
  return {
    content: (data as any).content ?? null,
    author: (data as any).profiles?.username ?? null,
  };
}

export async function resolveReport(
  reportId: string,
  status: Exclude<ReportStatus, "open">,
  notes?: string
): Promise<void> {
  const { data: me } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("reports")
    .update({
      status,
      reviewed_at: new Date().toISOString(),
      reviewed_by: me?.user?.id ?? null,
      admin_notes: notes?.trim() || null,
    })
    .eq("id", reportId);
  if (error) throw new Error(error.message);
}

// ── Orders ──────────────────────────────────────────────────────────────────

export async function setOrderStatus(
  orderId: string,
  status: OrderStatus
): Promise<void> {
  const { error } = await supabase.rpc("admin_set_order_status", {
    p_order: orderId,
    p_status: status,
  });
  if (error) throw new Error(error.message);
}
